import { Brain, Globe2, Cpu, Users } from "lucide-react";

const LandingPage = () => {
    return (
        <div className="bg-white text-gray-900 min-h-screen flex flex-col">
            {/* Hero Section */}
            <header className="flex flex-col items-center justify-center text-center py-20 px-6 bg-gray-50 border-b border-gray-200">
                <h1 className="text-5xl md:text-6xl font-extrabold mb-6 text-gray-900">
                    🌌 Step Into the Future of AI & Metaverse
                </h1>
                <p className="text-lg md:text-xl max-w-3xl mb-8 text-gray-600">
                    Discover the power of <span className="font-bold text-primary">Artificial Intelligence </span>
                    and the limitless potential of the <span className="font-bold text-primary">Metaverse</span>.
                    Connect, create, and innovate in a new digital frontier.
                </p>
                <div className="space-x-4">
                    <a
                        href="#/register"
                        className="bg-primary hover:bg-blue-800 hover:text-secondary text-white font-semibold py-3 px-8 rounded-lg shadow-lg transition-colors"
                    >
                        Try Hiiai
                    </a>
                </div>
            </header>

            {/* Features Section */}
            <section className="py-16 px-8 bg-white">
                <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
                    🚀 Why Choose Us?
                </h2>
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto">
                    <div className="p-6 bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                        <Brain className="w-10 h-10 mb-4 text-primary" />
                        <h3 className="text-xl font-semibold mb-2 text-gray-900">AI Powered</h3>
                        <p className="text-gray-600">
                            Leverage state-of-the-art AI for smarter decisions, automation,
                            and creativity.
                        </p>
                    </div>
                    <div className="p-6 bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                        <Globe2 className="w-10 h-10 mb-4 text-primary" />
                        <h3 className="text-xl font-semibold mb-2 text-gray-900">Immersive Metaverse</h3>
                        <p className="text-gray-600">
                            Explore and connect in fully immersive 3D virtual worlds.
                        </p>
                    </div>
                    <div className="p-6 bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                        <Cpu className="w-10 h-10 mb-4 text-primary" />
                        <h3 className="text-xl font-semibold mb-2 text-gray-900">Cutting Edge Tech</h3>
                        <p className="text-gray-600">
                            Built with the latest in Web3, VR, and AI for unmatched
                            experiences.
                        </p>
                    </div>
                    <div className="p-6 bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                        <Users className="w-10 h-10 mb-4 text-primary" />
                        <h3 className="text-xl font-semibold mb-2 text-gray-900">Global Community</h3>
                        <p className="text-gray-600">
                            Connect with innovators, creators, and visionaries from around the
                            globe.
                        </p>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section className="py-20 px-8 text-center bg-gray-50 border-y border-gray-200">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">🌐 About the Future</h2>
                <p className="max-w-4xl mx-auto text-gray-600 leading-relaxed">
                    The convergence of AI and the Metaverse is transforming the way we
                    live, work, and play. Artificial Intelligence empowers us to automate,
                    predict, and enhance human creativity, while the Metaverse provides a
                    new space for collaboration, exploration, and limitless imagination.
                    Together, they redefine the boundaries of technology and human
                    interaction.
                </p>
            </section>

            {/* Future We're Building */}
            <section className="py-20 px-8 bg-white text-center">
                <h2 className="text-3xl font-bold mb-12 text-gray-900">🔮 The Future We’re Building</h2>
                <div className="grid gap-10 md:grid-cols-3 max-w-6xl mx-auto">
                    <div className="p-8 bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                        <h3 className="text-xl font-semibold mb-3 text-gray-900">
                            Virtual Learning
                        </h3>
                        <p className="text-gray-600">
                            Imagine classrooms without borders, where students and teachers
                            collaborate in real-time 3D environments powered by AI tutors.
                        </p>
                    </div>
                    <div className="p-8 bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                        <h3 className="text-xl font-semibold mb-3 text-gray-900">
                            AI Avatars
                        </h3>
                        <p className="text-gray-600">
                            Create lifelike AI-driven avatars that represent you across
                            virtual worlds, handling communication, work, and play.
                        </p>
                    </div>
                    <div className="p-8 bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                        <h3 className="text-xl font-semibold mb-3 text-gray-900">
                            Smart Cities
                        </h3>
                        <p className="text-gray-600">
                            AI and Metaverse integration will revolutionize how cities operate
                            — from traffic control to community engagement in immersive ways.
                        </p>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-20 px-8 bg-gray-50 border-y border-gray-200 text-center">
                <h2 className="text-3xl font-bold mb-12 text-gray-900">💬 What People Are Saying</h2>
                <div className="grid gap-10 md:grid-cols-3 max-w-6xl mx-auto">
                    <div className="p-6 bg-white border border-gray-200 rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                        <p className="text-gray-600 italic mb-4">
                            “This platform is redefining how humans and AI collaborate — the
                            future feels closer than ever.”
                        </p>
                        <h4 className="font-semibold text-gray-900">– Dr. Alex Rivera</h4>
                        <p className="text-sm text-gray-500">AI Researcher</p>
                    </div>
                    <div className="p-6 bg-white border border-gray-200 rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                        <p className="text-gray-600 italic mb-4">
                            “The Metaverse experience is absolutely stunning. It’s like
                            walking inside your imagination.”
                        </p>
                        <h4 className="font-semibold text-gray-900">– Maya Chen</h4>
                        <p className="text-sm text-gray-500">VR Enthusiast</p>
                    </div>
                    <div className="p-6 bg-white border border-gray-200 rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                        <p className="text-gray-600 italic mb-4">
                            “I can see entire industries transforming with this technology —
                            education, entertainment, even governance.”
                        </p>
                        <h4 className="font-semibold text-gray-900">– James Carter</h4>
                        <p className="text-sm text-gray-500">Tech Entrepreneur</p>
                    </div>
                </div>
            </section>

            {/* Roadmap Section */}
            <section className="py-20 px-8 bg-white text-center">
                <h2 className="text-3xl font-bold mb-12 text-gray-900">🛤️ Our Roadmap</h2>
                <div className="grid gap-8 md:grid-cols-4 max-w-6xl mx-auto">
                    <div className="p-6 bg-white border border-gray-200 rounded-xl shadow-lg hover:-translate-y-1 transition-all duration-300">
                        <h3 className="text-xl font-semibold text-primary mb-2">Step 1</h3>
                        <p className="text-gray-600">Sign up and create your digital identity.</p>
                    </div>
                    <div className="p-6 bg-white border border-gray-200 rounded-xl shadow-lg hover:-translate-y-1 transition-all duration-300">
                        <h3 className="text-xl font-semibold text-primary mb-2">Step 2</h3>
                        <p className="text-gray-600">Explore immersive AI-driven metaverse spaces.</p>
                    </div>
                    <div className="p-6 bg-white border border-gray-200 rounded-xl shadow-lg hover:-translate-y-1 transition-all duration-300">
                        <h3 className="text-xl font-semibold text-primary mb-2">Step 3</h3>
                        <p className="text-gray-600">Collaborate, learn, and innovate with others.</p>
                    </div>
                    <div className="p-6 bg-white border border-gray-200 rounded-xl shadow-lg hover:-translate-y-1 transition-all duration-300">
                        <h3 className="text-xl font-semibold text-primary mb-2">Step 4</h3>
                        <p className="text-gray-600">Shape the future — build your own AI + Metaverse projects.</p>
                    </div>
                </div>
            </section>

            {/* Newsletter Section */}
            <section className="py-20 px-8 bg-gray-50 border-t border-gray-200 text-center">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">📩 Stay in the Loop</h2>
                <p className="text-gray-600 max-w-2xl mx-auto mb-8">
                    Join our community to receive updates about AI breakthroughs,
                    metaverse launches, and exclusive early access opportunities.
                </p>
                <form className="flex flex-col md:flex-row items-center justify-center gap-4 max-w-xl mx-auto">
                    <input
                        type="email"
                        placeholder="Enter your email"
                        className="w-full md:w-2/3 p-3 rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                        type="submit"
                        className="bg-primary hover:bg-blue-800 text-white font-semibold py-3 px-8 rounded-lg shadow-lg transition-colors"
                    >
                        Subscribe
                    </button>
                </form>
            </section>

            {/* Call To Action */}
            <footer className="py-12 text-center bg-white border-t border-gray-200">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">
                    🌟 Ready to Begin Your Journey?
                </h3>
                <p className="text-gray-600 mb-6">
                    Sign up today and be part of the AI & Metaverse revolution.
                </p>
                <a
                    href="/register"
                    className="bg-primary hover:bg-blue-800 text-white font-semibold py-3 px-10 rounded-lg shadow-lg transition-colors"
                >
                    Join Now
                </a>
            </footer>
        </div>
    );
};

export default LandingPage;
